import { useRef, type CSSProperties } from "react";
import { SC } from "./studioChrome";
import { DEFAULT_CHROMA } from "./chromaKey";
import type { Background, ChromaState } from "./api";

// Shared background-compositing control panel (ADR 0070/0071), in the RelayStudio
// (studioChrome) language (ADR 0120, resolving the ADR 0088 follow-up) so it reads the
// same in BOTH operator consoles — the Legend Go-Live Background tab AND the Director
// Chroma-key tab (the Director's hand-rolled KEY CONTROLS are gone; this IS the shared
// control surface). Pure UI: the parent owns the compositor lifecycle, the library
// fetch, the debounced relay (onTune), and — for the eyedropper — the video the click
// samples. Chroma-specific controls (key color, eyedropper, similarity / smoothness /
// spill) are hidden in ML mode (segmentation has no key color).

const labelStyle: CSSProperties = { fontFamily: SC.mono, fontSize: 9, letterSpacing: "0.06em", color: SC.dim };
const valueStyle: CSSProperties = { fontFamily: SC.mono, fontSize: 10, letterSpacing: "0.04em", color: SC.dim2 };
const fieldLabel: CSSProperties = { display: "block", fontFamily: SC.mono, fontSize: 9, letterSpacing: "0.06em", color: SC.dim, marginBottom: 6 };
const selectStyle: CSSProperties = { width: "100%", padding: "8px 10px", borderRadius: 9, border: `1px solid ${SC.hair2}`, background: SC.meterBg, color: SC.text, fontFamily: SC.sans, fontSize: 13 };

// A studioChrome pill button — `accent` (blue, active) vs `muted` (neutral chip).
function scBtn(accent: boolean): CSSProperties {
  return {
    padding: "8px 13px",
    borderRadius: 9,
    border: `1px solid ${accent ? SC.blueEdge : SC.hair3}`,
    background: accent ? SC.blueTint : SC.chipBg,
    color: accent ? SC.blueHi : "#e5e5ea",
    fontFamily: SC.sans,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.02em",
    cursor: "pointer",
  };
}

export interface ChromaControlsProps {
  chroma: ChromaState | undefined;
  backgrounds: Background[];
  bgBusy?: boolean;
  /** e.g. "Director override" / "set by Legend" — shown next to the title. */
  statusLabel?: string;
  onSelectBackground: (id: string) => void;
  onToggleEnabled: () => void;
  onSetMode: (mode: "chroma" | "ml") => void;
  /** Immediate set (discrete controls: fit). */
  onSet: (patch: Partial<ChromaState>) => void;
  /** Debounced-by-parent set (continuous controls: color + sliders). */
  onTune: (patch: Partial<ChromaState>) => void;
  /** Library management (optional — omit to hide Add/Remove). */
  onUpload?: (file: File) => void;
  onRemove?: (id: string) => void;
  /** Eyedropper (optional — omit to hide the Pick button). */
  eyedropper?: { armed: boolean; onToggle: () => void };
  /**
   * A prominent alert shown under the header — e.g. "a background is on but can't run
   * on this machine, the raw green screen is on air" (no GPU / WebGL), or a softer
   * "running without GPU acceleration" advisory (LSC-623 follow-up). Only the Legend
   * surface runs the compositor, so only it passes this.
   */
  warning?: string | null;
}

export default function ChromaControls({
  chroma,
  backgrounds,
  bgBusy,
  statusLabel,
  onSelectBackground,
  onToggleEnabled,
  onSetMode,
  onSet,
  onTune,
  onUpload,
  onRemove,
  eyedropper,
  warning,
}: ChromaControlsProps) {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const mode = chroma?.mode ?? "chroma";
  const isML = mode === "ml";

  return (
    <section aria-label="Background" style={{ display: "grid", gap: 10, maxWidth: 440 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span style={{ ...labelStyle, color: chroma?.enabled ? SC.blueHi : SC.dim }}>
          BACKGROUND{chroma?.enabled ? " — ON" : ""}
        </span>
        {statusLabel && (
          <span style={{ ...valueStyle, color: SC.redSoft }} role="status">
            {statusLabel}
          </span>
        )}
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          {onUpload && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                style={{ display: "none" }}
                aria-hidden="true"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) onUpload(f);
                }}
              />
              <button type="button" aria-label="Add background" disabled={bgBusy} onClick={() => fileRef.current?.click()} style={{ ...scBtn(false), opacity: bgBusy ? 0.6 : 1 }}>
                {bgBusy ? "Uploading…" : "Add background"}
              </button>
            </>
          )}
          <button
            type="button"
            aria-label={chroma?.enabled ? "Turn background off" : "Use background"}
            disabled={backgrounds.length === 0 && !chroma?.backgroundId}
            onClick={onToggleEnabled}
            style={{ ...scBtn(!!chroma?.enabled), opacity: backgrounds.length === 0 && !chroma?.backgroundId ? 0.5 : 1 }}
          >
            {chroma?.enabled ? "Turn off" : "Use background"}
          </button>
        </div>
      </div>

      {warning && (
        <div
          role="alert"
          style={{
            background: SC.redTint,
            border: `1px solid ${SC.redEdge}`,
            color: SC.redSoft,
            borderRadius: 8,
            padding: "8px 10px",
            fontSize: 12,
            lineHeight: 1.45,
          }}
        >
          {warning}
        </div>
      )}

      <label style={{ display: "block" }}>
        <span style={fieldLabel}>Mode</span>
        <select aria-label="Mode" value={mode} onChange={(e) => onSetMode(e.target.value as "chroma" | "ml")} style={selectStyle}>
          <option value="chroma">Green screen (chroma key)</option>
          <option value="ml">Auto (no green screen)</option>
        </select>
      </label>

      {backgrounds.length === 0 ? (
        <p style={valueStyle}>
          No backgrounds yet — add one to {isML ? "drop in behind you." : "key out your green screen."}
        </p>
      ) : (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }} role="list" aria-label="Background library">
          {backgrounds.map((b) => {
            const selected = chroma?.backgroundId === b.id;
            return (
              <div key={b.id} style={{ position: "relative" }} role="listitem">
                <button
                  type="button"
                  aria-label={`Background ${b.name}${selected ? " (selected)" : ""}`}
                  aria-pressed={selected}
                  onClick={() => onSelectBackground(b.id)}
                  style={{
                    width: 96,
                    height: 54,
                    padding: 0,
                    cursor: "pointer",
                    borderRadius: 6,
                    border: selected ? `2px solid ${SC.blue}` : `1px solid ${SC.hair2}`,
                    background: `center/cover no-repeat url(${b.imageUrl}), ${SC.panel2}`,
                  }}
                />
                {onRemove && (
                  <button
                    type="button"
                    aria-label={`Remove background ${b.name}`}
                    onClick={() => onRemove(b.id)}
                    style={{
                      position: "absolute",
                      top: -6,
                      right: -6,
                      width: 18,
                      height: 18,
                      lineHeight: "16px",
                      padding: 0,
                      borderRadius: "50%",
                      border: `1px solid ${SC.hair2}`,
                      background: SC.panel,
                      color: SC.text,
                      cursor: "pointer",
                      fontSize: 12,
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Fit applies to both modes; key tuning is chroma-only. */}
      {chroma?.backgroundId && (
        <div style={{ display: "grid", gap: 10 }} aria-label="Key tuning">
          <div style={{ display: "flex", alignItems: "flex-end", gap: 10, flexWrap: "wrap" }}>
            <label style={{ display: "block", minWidth: 132 }}>
              <span style={fieldLabel}>Fit</span>
              <select aria-label="Fit" value={chroma?.fit ?? "cover"} onChange={(e) => onSet({ fit: e.target.value as "cover" | "contain" | "stretch" })} style={selectStyle}>
                <option value="cover">Cover</option>
                <option value="contain">Contain</option>
                <option value="stretch">Stretch</option>
              </select>
            </label>
            {!isML && (
              <>
                <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <span style={labelStyle}>Key color</span>
                  <input
                    type="color"
                    aria-label="Key color"
                    value={chroma?.color ?? DEFAULT_CHROMA.color}
                    onChange={(e) => onTune({ color: e.target.value })}
                    style={{ width: 48, height: 32, padding: 0, border: `1px solid ${SC.hair2}`, background: "none", borderRadius: 6 }}
                  />
                </label>
                {eyedropper && (
                  <button
                    type="button"
                    aria-label={eyedropper.armed ? "Cancel eyedropper" : "Pick key color from video"}
                    onClick={eyedropper.onToggle}
                    style={scBtn(eyedropper.armed)}
                  >
                    {eyedropper.armed ? "Click the video…" : "Pick from video"}
                  </button>
                )}
                <button
                  type="button"
                  aria-label="Reset key tuning"
                  onClick={() =>
                    onTune({
                      color: DEFAULT_CHROMA.color,
                      similarity: DEFAULT_CHROMA.similarity,
                      smoothness: DEFAULT_CHROMA.smoothness,
                      spill: DEFAULT_CHROMA.spill,
                    })
                  }
                  style={scBtn(false)}
                >
                  Reset
                </button>
              </>
            )}
          </div>
          {!isML &&
            (
              [
                ["similarity", "Similarity"],
                ["smoothness", "Smoothness"],
                ["spill", "Spill"],
              ] as const
            ).map(([key, label]) => (
              <label key={key} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ ...labelStyle, minWidth: 84 }}>{label}</span>
                <input
                  type="range"
                  aria-label={label}
                  min={0}
                  max={key === "similarity" ? 1 : 0.5}
                  step={0.01}
                  value={chroma?.[key] ?? DEFAULT_CHROMA[key]}
                  onChange={(e) => onTune({ [key]: Number(e.target.value) })}
                  style={{ flex: 1, accentColor: SC.blue }}
                />
                <span style={{ ...valueStyle, minWidth: 32, color: SC.text }}>
                  {(chroma?.[key] ?? DEFAULT_CHROMA[key]).toFixed(2)}
                </span>
              </label>
            ))}
        </div>
      )}
    </section>
  );
}
